import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// A WWI field medic — a rig-compatible figure on the same skeleton and
// proportions as the standard actor (`worker-simple`) and the infantryman, so
// every interaction and station program fits it unchanged. Its parts carry the
// actor rig names (`body`, `head`, `arm-l`, `arm-r`, `leg-l`, `leg-r`; the
// whole figure moves via the rig's `root`). Modelled facing +Z, feet on the
// floor.
//
// Look: a pale canvas-khaki uniform, a brodie helmet with a red cross painted
// on top, a white red-cross armband on the right arm, and a red-cross satchel
// on the left hip. The tunic and sleeves are tagged `slot: "team"`, so the
// canvas tone is only the default and a consumer recolours the uniform per
// side; the crosses keep their colours.
const UNIFORM = "#cbbd93";    // canvas-khaki tunic (the `team` slot's default)
const TROUSER = "#a89a76";    // trousers, a shade darker
const PUTTEE = "#8a7d5a";     // wool puttee wraps
const BOOT = "#3a2e20";
const SKIN = "#d9a06b";
const STEEL = "#6e7266";      // brodie helmet
const WHITE = "#e8e4da";      // armband / satchel panel
const CROSS = "#c0392b";      // red cross
const STRAP = "#4a3a28";      // leather straps

const LEG_H = 0.16;
const BODY_Y = LEG_H + 0.11;       // torso centre, stacked on the legs
const SHOULDER_Y = BODY_Y + 0.07;  // arm hinge height
const HEAD_Y = BODY_Y + 0.12 + 0.06;

// Animation pivots (model-local): neck, torso centre, shoulders, hips.
const E = "sine" as const;
const HP = [0, HEAD_Y - 0.05, 0] as const;
const BP = [0, BODY_Y, 0] as const;
const ALP = [-0.115, SHOULDER_Y, 0] as const;
const ARP = [0.115, SHOULDER_Y, 0] as const;
const LLP = [-0.05, LEG_H, 0] as const;
const LRP = [0.05, LEG_H, 0] as const;

// A trousered, puttee-wrapped leg ending in a boot.
function leg(name: string, x: number): Part[] {
  return [
    { geometry: new THREE.BoxGeometry(0.07, 0.09, 0.08), color: TROUSER, position: [x, 0.115, 0], name },
    { geometry: new THREE.BoxGeometry(0.072, 0.07, 0.082), color: PUTTEE, position: [x, 0.055, 0], name },
    { geometry: new THREE.BoxGeometry(0.075, 0.04, 0.11), color: BOOT, position: [x, 0.02, 0.015], name },
  ];
}

function medicParts(): Part[] {
  const HAND_Y = SHOULDER_Y - 0.175;
  return [
    ...leg("leg-l", -0.05),
    ...leg("leg-r", 0.05),
    // Canvas-khaki tunic (the team-colour channel) over a leather belt.
    { geometry: new THREE.BoxGeometry(0.18, 0.22, 0.11), color: UNIFORM, position: [0, BODY_Y, 0], name: "body", slot: "team" },
    { geometry: new THREE.BoxGeometry(0.185, 0.035, 0.115), color: STRAP, position: [0, LEG_H + 0.03, 0], name: "body" },
    // Left arm: plain sleeve and hand.
    { geometry: new THREE.BoxGeometry(0.045, 0.18, 0.05), color: UNIFORM, position: [-0.115, SHOULDER_Y - 0.07, 0], name: "arm-l", slot: "team" },
    { geometry: new THREE.BoxGeometry(0.04, 0.035, 0.045), color: SKIN, position: [-0.115, HAND_Y, 0], name: "arm-l" },
    // Right arm: sleeve, hand, and the white red-cross armband wrapping the
    // upper sleeve with a red cross on its outer face.
    { geometry: new THREE.BoxGeometry(0.045, 0.18, 0.05), color: UNIFORM, position: [0.115, SHOULDER_Y - 0.07, 0], name: "arm-r", slot: "team" },
    { geometry: new THREE.BoxGeometry(0.04, 0.035, 0.045), color: SKIN, position: [0.115, HAND_Y, 0], name: "arm-r" },
    { geometry: new THREE.BoxGeometry(0.056, 0.05, 0.06), color: WHITE, position: [0.115, SHOULDER_Y - 0.045, 0], name: "arm-r" },
    { geometry: new THREE.BoxGeometry(0.012, 0.038, 0.012), color: CROSS, position: [0.145, SHOULDER_Y - 0.045, 0], name: "arm-r" },
    { geometry: new THREE.BoxGeometry(0.012, 0.012, 0.038), color: CROSS, position: [0.145, SHOULDER_Y - 0.045, 0], name: "arm-r" },
    // Red-cross satchel on the left hip, slung from a shoulder strap.
    { geometry: new THREE.BoxGeometry(0.03, 0.21, 0.012), color: STRAP,
      position: [-0.02, BODY_Y + 0.015, 0.052], rotation: [0, 0, -0.5], name: "body" },
    { geometry: new THREE.BoxGeometry(0.045, 0.09, 0.09), color: WHITE, position: [-0.105, BODY_Y - 0.1, 0.02], name: "body" },
    { geometry: new THREE.BoxGeometry(0.012, 0.056, 0.016), color: CROSS, position: [-0.13, BODY_Y - 0.1, 0.02], name: "body" },
    { geometry: new THREE.BoxGeometry(0.012, 0.016, 0.056), color: CROSS, position: [-0.13, BODY_Y - 0.1, 0.02], name: "body" },
    // Head under the brodie helmet, a red cross painted on the dome's crown.
    { geometry: new THREE.BoxGeometry(0.11, 0.11, 0.11), color: SKIN, position: [0, HEAD_Y, 0], name: "head" },
    { geometry: new THREE.CylinderGeometry(0.095, 0.095, 0.014, 10), color: STEEL,
      position: [0, HEAD_Y + 0.045, 0], name: "head" },
    { geometry: new THREE.SphereGeometry(0.078, 10, 6), color: STEEL,
      position: [0, HEAD_Y + 0.048, 0], scale: [1, 0.55, 1], name: "head" },
    { geometry: new THREE.BoxGeometry(0.066, 0.008, 0.022), color: CROSS, position: [0, HEAD_Y + 0.09, 0], name: "head" },
    { geometry: new THREE.BoxGeometry(0.022, 0.008, 0.066), color: CROSS, position: [0, HEAD_Y + 0.09, 0], name: "head" },
  ];
}

export const medic = defineModel({
  id: "medic",
  batchId: "wwi",
  author: "Fable 5",
  name: "Medic",
  tags: ["wwi", "character", "actor", "humanoid", "person", "soldier", "military", "medic", "medical"],
  summary: "A rig-compatible WWI medic — pale canvas uniform, red-cross armband, hip satchel and a cross-marked brodie helmet.",
  description:
    "The Great War stretcher-bearer and field medic: a rig-compatible actor on " +
    "the standard worker skeleton, in a pale canvas-khaki uniform (the " +
    "team-colour channel) with a white red-cross armband on the right arm, a " +
    "red-cross satchel slung to the left hip and a brodie helmet with a red " +
    "cross painted on top. Any interaction or station program that poses the " +
    "standard actor fits it unchanged.",
  footprint: 0.14,
  tiles: { w: 1, h: 1 },
  parts: medicParts,
  // Where a carried good is cradled in front of the chest, matching the other
  // avatars; the good is supplied by the consumer at bake time.
  sockets: [
    { id: "carry", position: [0, 0.24, 0.13], slotType: "carry", accepts: ["carriable"],
      description: "A good carried in front of the chest." },
  ],
  animations: [
    { id: "idle", label: "Idle", duration: 2.0, tracks: [
      { target: "body", pivot: BP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 1, value: [0.02, 0, 0], ease: E }, { t: 2, value: [0, 0, 0], ease: E }] },
      { target: "head", pivot: HP, position: [
        { t: 0, value: [0, 0, 0] }, { t: 1, value: [0, 0.006, 0], ease: E }, { t: 2, value: [0, 0, 0], ease: E }] },
      { target: "arm-l", pivot: ALP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 1.3, value: [0, 0, -0.05], ease: E }, { t: 2, value: [0, 0, 0], ease: E }] },
      { target: "arm-r", pivot: ARP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 1.3, value: [0, 0, 0.05], ease: E }, { t: 2, value: [0, 0, 0], ease: E }] },
    ] },
    { id: "walk", label: "Walk", duration: 0.8, tracks: [
      { target: "body", pivot: BP, rotation: [
        { t: 0, value: [0.04, 0, 0.02] }, { t: 0.4, value: [0.04, 0, -0.02], ease: E }, { t: 0.8, value: [0.04, 0, 0.02], ease: E }] },
      { target: "leg-l", pivot: LLP, rotation: [
        { t: 0, value: [0.35, 0, 0] }, { t: 0.4, value: [-0.35, 0, 0], ease: E }, { t: 0.8, value: [0.35, 0, 0], ease: E }] },
      { target: "leg-r", pivot: LRP, rotation: [
        { t: 0, value: [-0.35, 0, 0] }, { t: 0.4, value: [0.35, 0, 0], ease: E }, { t: 0.8, value: [-0.35, 0, 0], ease: E }] },
      { target: "arm-l", pivot: ALP, rotation: [
        { t: 0, value: [-0.3, 0, 0] }, { t: 0.4, value: [0.3, 0, 0], ease: E }, { t: 0.8, value: [-0.3, 0, 0], ease: E }] },
      { target: "arm-r", pivot: ARP, rotation: [
        { t: 0, value: [0.3, 0, 0] }, { t: 0.4, value: [-0.3, 0, 0], ease: E }, { t: 0.8, value: [0.3, 0, 0], ease: E }] },
      { target: "head", pivot: HP, position: [
        { t: 0, value: [0, 0, 0] }, { t: 0.2, value: [0, 0.006, 0], ease: E }, { t: 0.4, value: [0, 0, 0], ease: E },
        { t: 0.6, value: [0, 0.006, 0], ease: E }, { t: 0.8, value: [0, 0, 0], ease: E }] },
    ] },
    { id: "march", label: "March", duration: 1.0, tracks: [
      { target: "body", pivot: BP, rotation: [
        { t: 0, value: [0, 0, 0.03] }, { t: 0.5, value: [0, 0, -0.03], ease: E }, { t: 1.0, value: [0, 0, 0.03], ease: E }] },
      { target: "leg-l", pivot: LLP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.25, value: [-0.6, 0, 0], ease: E }, { t: 0.5, value: [0, 0, 0], ease: E }] },
      { target: "leg-r", pivot: LRP, rotation: [
        { t: 0.5, value: [0, 0, 0] }, { t: 0.75, value: [-0.6, 0, 0], ease: E }, { t: 1.0, value: [0, 0, 0], ease: E }] },
      { target: "arm-l", pivot: ALP, rotation: [
        { t: 0, value: [0.35, 0, 0] }, { t: 0.5, value: [-0.35, 0, 0], ease: E }, { t: 1.0, value: [0.35, 0, 0], ease: E }] },
      { target: "arm-r", pivot: ARP, rotation: [
        { t: 0, value: [-0.35, 0, 0] }, { t: 0.5, value: [0.35, 0, 0], ease: E }, { t: 1.0, value: [-0.35, 0, 0], ease: E }] },
    ] },
    { id: "salute", label: "Salute", duration: 1.5, loop: false, tracks: [
      { target: "body", pivot: BP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.3, value: [-0.04, 0, 0], ease: E },
        { t: 1.1, value: [-0.04, 0, 0] }, { t: 1.5, value: [0, 0, 0], ease: E }] },
      { target: "arm-r", pivot: ARP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.3, value: [-1.8, 0.3, 0], ease: E },
        { t: 1.1, value: [-1.8, 0.3, 0] }, { t: 1.5, value: [0, 0, 0], ease: E }] },
      { target: "head", pivot: HP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.3, value: [0.05, 0, 0], ease: E },
        { t: 1.1, value: [0.05, 0, 0] }, { t: 1.5, value: [0, 0, 0], ease: E }] },
    ] },
  ],
});
